import * as Sentry from '@sentry/nextjs';
import Router from 'next/router';
import { type ErrorInfo, type ReactNode, Component } from 'react';
import { Button } from '@music-vine/cadence/ui';
interface Props {
  children: ReactNode;
}
interface State {
  error?: Error;
  hasError: boolean;
}

/**
 * App-level error boundary that catches unhandled rendering errors,
 * reports them to Sentry, and shows a full-page fallback UI.
 */
export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }
  componentDidMount(): void {
    // Recover automatically when the user navigates to a new route, so
    // backspacing or starting a new search clears a previously caught error
    // instead of leaving the fallback UI stuck on the view they navigated
    // away from.
    Router.events.on('routeChangeComplete', this.resetError);
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.withScope(scope => {
      scope.setTag('errorBoundary', 'app');
      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack
      });
      Sentry.captureException(error);
    });
  }
  componentWillUnmount(): void {
    Router.events.off('routeChangeComplete', this.resetError);
  }

  // Clear the caught error and re-render children when the user navigates to
  // a new route, so recovery no longer depends on a full document reload of a
  // different URL.
  resetError = (): void => {
    // Return null when there's nothing to reset so React bails out of the
    // update — returning the existing state object would still schedule a
    // re-render of the whole app on every navigation.
    this.setState(state => state.hasError ? {
      hasError: false,
      error: undefined
    } : null);
  };
  render(): ReactNode {
    if (this.state.hasError) {
      return <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-950" data-sentry-component="AppErrorBoundary" data-sentry-source-file="index.tsx">
          <div className="flex max-w-lg flex-col items-start gap-6 px-6">
            <img alt="Uppbeat" height={48} src="https://cdn.uppbeat.io/images/logo/uppbeat-logo.png?width=274" width={137} />
            <h1 className="font-semibold text-2xl text-gray-900 dark:text-gray-100">
              Something went wrong
            </h1>
            <p className="text-pretty text-gray-700 text-lg dark:text-gray-300">
              An unexpected error occurred. Please refresh the page to try
              again.
            </p>
            <div className="flex gap-3">
              <Button onClick={() => window.location.reload()} size="lg" variant="brand" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                Refresh page
              </Button>
              <Button onClick={() => {
              window.location.href = '/';
            }} size="lg" variant="light" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                Back to home
              </Button>
            </div>
          </div>
        </div>;
    }
    return this.props.children;
  }
}